import { useEffect, useMemo, useState, useDeferredValue } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Search, ChevronLeft } from "lucide-react";
import { CustomerRow, matchCustomer, fmtDate } from "../supervisor/MandoobCustomers";
import CustomerStatement from "./CustomerStatement";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const ymd = (d) => new Date(d.getTime() + 3 * 3600e3).toISOString().slice(0, 10);
const monthStart = () => { const n = new Date(Date.now() + 3 * 3600e3); return `${n.toISOString().slice(0, 8)}01`; };

const Box = ({ label, value, color, testid }) => (
  <div className="rounded-2xl p-3 text-center" style={{ background: `${color}15` }} data-testid={testid}>
    <p className="text-[10px] font-bold" style={{ color }}>{label}</p>
    <p className="font-black text-base" style={{ color }} dir="ltr">{value}</p>
  </div>
);

const CustomerDetail = ({ mid, c, onBack }) => {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(ymd(new Date()));
  const [sum, setSum] = useState(null);
  useEffect(() => {
    setSum(null);
    axios.get(`${API}/admin/mandoobs/${mid}/customers/${c.id}/summary`, {
      params: { date_from: from ? `${from}T00:00:00+03:00` : undefined, date_to: to ? `${to}T23:59:59+03:00` : undefined },
    }).then(({ data }) => setSum(data)).catch(() => toast.error("فشل التحميل"));
  }, [mid, c.id, from, to]);
  return (
    <div className="space-y-3" data-testid="mandoob-customer-detail">
      <button onClick={onBack} data-testid="mandoob-customer-back" className="text-sm font-bold text-[#1E3A8A] flex items-center gap-1">
        <ChevronLeft className="w-4 h-4 rotate-180" /> الزبائن
      </button>
      <CustomerRow c={c} />
      <div className="flex items-center gap-2 text-xs">
        <span className="font-bold">من</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="mcd-from" className="border-2 rounded-xl px-2 py-1.5 flex-1" />
        <span className="font-bold">إلى</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="mcd-to" className="border-2 rounded-xl px-2 py-1.5 flex-1" />
      </div>
      {!sum ? <Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-700" /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Box label="قطع بهاي الفترة" value={fmt(sum.invoices_total)} color="#1E3A8A" testid="mcd-total" />
            <Box label="عدد الفواتير" value={sum.invoices_count} color="#7C3AED" testid="mcd-count" />
            <Box label="سدّد بهاي الفترة" value={fmt(sum.paid_total)} color="#16A34A" testid="mcd-paid" />
            <Box label="آخر فاتورة" value={fmtDate(sum.last_invoice_at)} color="#D97706" testid="mcd-last" />
          </div>
          <div className="bg-slate-50 rounded-2xl divide-y max-h-48 overflow-y-auto">
            {sum.invoices.map((o) => (
              <div key={o.id} className="flex justify-between px-3 py-2 text-xs">
                <span className="font-bold">#{o.invoice_number}</span><span>{fmtDate(o.created_at)}</span>
                <span className="font-black" dir="ltr">{fmt(o.total)}</span>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="border-t pt-3"><p className="font-black text-sm mb-2">كشف الحساب الكامل</p>
        <CustomerStatement initialCustomer={{ id: c.id, full_name: c.full_name, phone: c.phone, market_name: c.market_name }} />
      </div>
    </div>
  );
};

const MandoobCustomersModal = ({ mandoob, onClose }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const dq = useDeferredValue(q);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/mandoobs/${mandoob.id}/customers`).then(({ data }) => setRows(data)).catch(() => toast.error("فشل التحميل"));
  }, [mandoob.id]);
  const list = useMemo(() => (rows || []).filter((c) => matchCustomer(c, dq)), [rows, dq]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" dir="rtl" data-testid="mandoob-customers-modal">
      <div className="bg-white w-full sm:max-w-3xl h-[94vh] flex flex-col rounded-t-3xl sm:rounded-3xl overflow-hidden">
        <div className="p-4 flex items-center justify-between text-white" style={{ background: "linear-gradient(135deg,#0EA5E9,#0369A1)" }}>
          <div><p className="font-black">زبائن {mandoob.full_name}</p><p className="text-[11px] opacity-85">{rows ? rows.length : "…"} زبون</p></div>
          <button onClick={onClose} data-testid="mandoob-customers-close" className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
          {open ? <CustomerDetail mid={mandoob.id} c={open} onBack={() => setOpen(null)} /> : (
            <>
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم، الماركت، العنوان أو الرقم..."
                  data-testid="admin-mandoob-customers-search" className="w-full pr-9 pl-3 py-2.5 rounded-2xl border-2 border-slate-200 text-sm outline-none" />
              </div>
              {!rows ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-sky-600" /> : list.map((c) => (
                <CustomerRow key={c.id} c={c} onClick={() => setOpen(c)}
                  extra={<p className="text-[11px] font-bold text-sky-700">قطع هذا الشهر: <span dir="ltr">{fmt(c.month_total)}</span> د.ع</p>} />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MandoobCustomersModal;
